import { useEffect, useState } from 'react';
import { getAdminBookings, updateAdminBooking, getDrivers, adminDownloadReceipt } from '../../services/api';
import toast from 'react-hot-toast';
import { Search, Filter, Download } from 'lucide-react';

const StatusBadge = ({ status }) => <span className={`badge-${status}`}>{status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>;

const STATUSES = ['all', 'pending', 'confirmed', 'assigned', 'in_progress', 'completed', 'cancelled'];

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [updateForm, setUpdateForm] = useState({});
  const [downloading, setDownloading] = useState(null);

  const handleDownload = async (b) => {
    setDownloading(b.uuid);
    try {
      const res = await adminDownloadReceipt(b.uuid);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `MDS-Receipt-${b.uuid.slice(0, 8).toUpperCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Receipt downloaded!');
    } catch {
      toast.error('Could not generate receipt.');
    } finally {
      setDownloading(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { status: filter, limit: 100 } : { limit: 100 };
      const [bookRes, driverRes] = await Promise.all([getAdminBookings(params), getDrivers()]);
      setBookings(bookRes.data.bookings);
      setDrivers(driverRes.data.drivers);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const filtered = bookings.filter(b =>
    !search || b.passenger_name?.toLowerCase().includes(search.toLowerCase()) || b.pickup_address?.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (b) => {
    setSelected(b);
    setUpdateForm({ status: b.status, driver_id: b.driver_id || '', admin_notes: b.admin_notes || '', pickup_time: b.pickup_time || '' });
  };

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      await updateAdminBooking(selected.uuid, updateForm);
      toast.success('Booking updated.');
      setSelected(null);
      load();
    } catch { toast.error('Update failed.'); }
    finally { setUpdating(false); }
  };

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-heading">Bookings Management</h1>
          <p className="text-sm text-gray-500">{bookings.length} total bookings</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="input-field pl-9 py-2.5 text-sm" placeholder="Search by passenger or address..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="flex flex-wrap gap-1">
          {STATUSES.map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-3 py-2 rounded-lg text-xs font-medium capitalize transition-colors ${filter === s ? 'bg-purple-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? <div className="flex justify-center h-40 items-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-700" /></div> : (
        <div className="card border border-gray-100 overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Passenger', 'Service', 'Pickup → Destination', 'Date & Time', 'Status', 'Driver', 'Actions'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-12 text-gray-400">No bookings found.</td></tr>
                ) : filtered.map(b => (
                  <tr key={b.uuid} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-gray-900">{b.passenger_name}</p>
                      <p className="text-xs text-gray-500">{b.passenger_phone}</p>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 capitalize whitespace-nowrap">{b.service_type?.replace(/_/g, ' ')}</td>
                    <td className="px-4 py-3">
                      <p className="text-xs text-gray-700">{b.pickup_city || b.pickup_address?.slice(0, 30)}</p>
                      <p className="text-xs text-gray-500">→ {b.destination_city || b.destination_address?.slice(0, 30)}</p>
                    </td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">
                      <p className="font-medium text-gray-800">{b.appointment_date}</p>
                      <p className="text-gray-500">{b.appointment_time?.slice(0, 5)}</p>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {b.driver_first ? `${b.driver_first} ${b.driver_last}` : <span className="text-gray-400">Unassigned</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleSelect(b)} className="text-xs text-purple-700 hover:text-purple-900 font-semibold border border-purple-200 hover:border-purple-400 px-2.5 py-1 rounded-lg transition-colors">
                          Manage
                        </button>
                        <button
                          onClick={() => handleDownload(b)}
                          disabled={downloading === b.uuid}
                          title="Download Receipt"
                          className="text-xs text-gray-500 hover:text-gray-800 border border-gray-200 hover:border-gray-400 p-1.5 rounded-lg transition-colors"
                        >
                          {downloading === b.uuid
                            ? <div className="w-3 h-3 border border-gray-400/40 border-t-gray-600 rounded-full animate-spin" />
                            : <Download size={13} />
                          }
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Booking update modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 font-heading">Manage Booking</h2>
              <p className="text-sm text-gray-500">{selected.passenger_name} · {selected.appointment_date}</p>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="label">Status</label>
                <select className="input-field" value={updateForm.status} onChange={e => setUpdateForm({ ...updateForm, status: e.target.value })}>
                  {['pending', 'confirmed', 'assigned', 'in_progress', 'completed', 'cancelled', 'no_show'].map(s => (
                    <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Assign Driver</label>
                <select className="input-field" value={updateForm.driver_id} onChange={e => setUpdateForm({ ...updateForm, driver_id: e.target.value })}>
                  <option value="">Unassigned</option>
                  {drivers.filter(d => d.is_active).map(d => (
                    <option key={d.id} value={d.id}>{d.first_name} {d.last_name} ({d.status})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Pickup Time</label>
                <input type="time" className="input-field" value={updateForm.pickup_time} onChange={e => setUpdateForm({ ...updateForm, pickup_time: e.target.value })} />
              </div>
              <div>
                <label className="label">Admin Notes</label>
                <textarea className="input-field min-h-[80px] resize-none" placeholder="Internal notes..." value={updateForm.admin_notes} onChange={e => setUpdateForm({ ...updateForm, admin_notes: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm bg-gray-50 rounded-xl p-4">
                <div><span className="text-gray-500">Mobility:</span> <span className="font-medium">{[selected.needs_wheelchair && '♿ Wheelchair', selected.needs_walker && '🦯 Walker'].filter(Boolean).join(', ') || 'None'}</span></div>
                <div><span className="text-gray-500">Type:</span> <span className="font-medium capitalize">{selected.service_type?.replace(/_/g, ' ')}</span></div>
                {selected.special_instructions && <div className="col-span-2"><span className="text-gray-500">Instructions:</span> <span className="font-medium">{selected.special_instructions}</span></div>}
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button onClick={() => setSelected(null)} className="btn-outline text-sm">Cancel</button>
              <button onClick={handleUpdate} disabled={updating} className="btn-primary text-sm flex items-center gap-2">
                {updating && <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
